/** @jsxRuntime automatic */
/** @jsxImportSource mini-jsx */
// import type makes sure we don't include this in the bundle by default
import type Uppy from '@uppy/core'
import type { Body, Meta, UppyOptions } from '@uppy/core'
import { BasePlugin } from '@uppy/core'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/audio/dist/style.css'
import '@uppy/image-editor/dist/style.css'
import '@uppy/screen-capture/dist/style.css'
import '@uppy/url/dist/style.css'
import '@uppy/webcam/dist/style.css'
import { ready } from '../sharedWithConsole/ready.ts'

class ProgressBarPlugin<M extends Meta, B extends Body> extends BasePlugin<
  UppyOptions<M, B>,
  M,
  B
> {
  progress: HTMLElement | null = null

  progressBar: HTMLElement | null = null

  progressBarText: HTMLElement | null = null

  title: string

  constructor(uppy: Uppy<M, B>, opts?: UppyOptions<M, B>) {
    super(uppy, opts)
    this.type = 'progressindicator'
    this.id = 'TransloaditDemosProgressBar'
    this.title = 'Transloadit Demos progress bar'
  }

  install() {
    this.progress = document.getElementById('encoding-progress')
    this.progressBar = this.progress?.querySelector('.progress-bar') || null
    this.progressBarText = this.progressBar?.querySelector('span') || null

    this.uppy.on('upload', () => {
      this.progress?.classList.remove('hidden')
      this.showEncoding()
    })
    this.uppy.on('upload-progress', () => {
      this.handleProgress()
    })
    this.uppy.on('upload-success', () => {
      this.showEncoding()
    })
    this.uppy.on('complete', () => {
      this.showSuccessText()
    })
    this.uppy.on('error', (err) => {
      this.handleError(err)
    })
  }

  resetProgressBar() {
    if (this.progressBar) {
      this.progressBar.style.width = '0%'
      this.progressBar.classList.remove('progress-bar-success')
      this.progressBar.classList.add('progress-bar-info')
    }
    if (this.progressBarText) {
      this.progressBarText.textContent = '0%'
    }
  }

  handleProgress() {
    const { files } = this.uppy.getState()
    const bytesReceived = Object.values(files).reduce((amount, file) => {
      return amount + ((file.progress?.bytesUploaded ?? 0) as number)
    }, 0)
    const bytesExpected = Object.values(files).reduce((amount, file) => {
      return amount + (file.size ?? 0)
    }, 0)

    let progress = (bytesReceived / bytesExpected) * 100
    if (progress > 100) {
      progress = 0
    }

    if (this.progressBar) {
      this.progressBar.style.width = `${progress}%`
    }
    if (this.progressBarText) {
      this.progressBarText.textContent = `${progress.toFixed(0)}%`
    }
  }

  showEncoding() {
    if (this.progressBar) {
      this.progressBar.style.width = '100%'
      this.progressBar.classList.add('active')
    }
    if (this.progressBarText) {
      this.progressBarText.innerHTML = 'Now processing media...'
    }
  }

  showSuccessText() {
    if (this.progressBar) {
      this.progressBar.style.width = '100%'
      this.progressBar.classList.remove('progress-bar-info', 'active')
      this.progressBar.classList.add('progress-bar-success')
    }
    if (this.progressBarText) {
      this.progressBarText.innerHTML = 'Success : )'
    }
  }

  handleError(err: Error) {
    if (this.progressBar) {
      this.progressBar.classList.remove('progress-bar-info', 'active')
      this.progressBar.classList.add('progress-bar-danger')
    }
    if (this.progressBarText) {
      this.progressBarText.innerHTML = 'Failed : ('
    }
    console.error(err)
  }
}

const uppyWrapper = document.querySelector('.js-uppy-wrapper')
const encodingResults = document.querySelector('.js-encoding-results')
const startingText = document.getElementById('encoding-progress')?.querySelector('.starting-text')
const uppyDashboard = document.getElementById('uppy-dashboard')
const runButton = document.getElementById('run-button')

ready(async () => {
  if (!window.demoAccountKey || !uppyDashboard) {
    return
  }

  if (!encodingResults) {
    throw new Error('No encoding results element found')
  }

  if (!(encodingResults instanceof HTMLElement)) {
    throw new Error('encodingResults is not an HTMLElement')
  }

  const { default: Uppy } = await import('@uppy/core')
  const { default: Dashboard } = await import('@uppy/dashboard')
  const { default: RemoteSources } = await import('@uppy/remote-sources')
  const { default: Webcam } = await import('@uppy/webcam')
  const { default: ScreenCapture } = await import('@uppy/screen-capture')
  const { default: ImageEditor } = await import('@uppy/image-editor')
  const { default: Audio } = await import('@uppy/audio')
  const { default: Transloadit, COMPANION_ALLOWED_HOSTS } = await import('@uppy/transloadit')

  const { default: TransloaditResults } = await import('./UppyTransloaditResultsPlugin.tsx')
  const { default: ClearOldFiles } = await import('./UppyClearOldFilesPlugin.ts')
  const { api2, companion } = await import('~/utils/Urls.ts')
  const { default: isProd } = await import('~/utils/isProd.ts')

  const uploadRequired = uppyDashboard.classList.contains('upload-required')

  const uppy = new Uppy({
    debug: true,
    restrictions: { maxFileSize: 100 * 1024 * 1024 },
  })
    .use(Dashboard, {
      target: '#uppy-dashboard',
      inline: true,
      width: '100%',
      replaceTargetContent: true,
      note: window.demoRunInstructions,
    })
    .use(RemoteSources, {
      sources: ['Box', 'Dropbox', 'GoogleDrive', 'OneDrive', 'Unsplash', 'Url'],
      companionUrl: companion(),
      companionAllowedHosts: isProd ? COMPANION_ALLOWED_HOSTS : /.*/,
    })
    .use(Webcam, {
      showRecordingLength: true,
    })
    .use(Audio)
    .use(ScreenCapture)
    .use(ImageEditor)
    .use(Transloadit, {
      service: api2(),
      waitForEncoding: true,
      alwaysRunAssembly: !uploadRequired,
      assemblyOptions: () => {
        const opts = {
          params: {
            template_id: window.demoTemplateId,
            auth: { key: window.demoAccountKey },
            emit_execution_progress: true,
          },
        }

        if (Array.isArray(window.demoInputFields)) {
          window.demoInputFields.forEach(({ destination, label }, index) => {
            const id = `demo-input-field-${index}`
            const el = document.getElementById(id)
            if (el == null) {
              console.log(`WARNING: No input element found for label ${label}`)
              return
            }

            if (!(el instanceof HTMLInputElement)) {
              console.log(`WARNING: Input element for label ${label} is not an HTMLInputElement`)
              return
            }

            const { value } = el
            setValueByPath(opts, destination, value)
          })
        }

        return opts
      },
    })

  if (!uploadRequired) {
    // Only show Run button (like, for import Assemblies)
    uppyDashboard.classList.add('hidden')
    const runButtonSpinner = runButton?.querySelector('.run-button-spinner')

    runButton?.addEventListener('click', (event) => {
      event.preventDefault()
      if (runButton instanceof HTMLButtonElement) {
        runButton.disabled = true
        // Show spinner
        runButtonSpinner?.classList.remove('hidden')

        uppy.upload().then(
          () => {
            runButton.disabled = false
            // Hide spinner
            runButtonSpinner?.classList.add('hidden')
          },
          () => {
            runButton.disabled = false
            // Hide spinner
            runButtonSpinner?.classList.add('hidden')
          },
        )
      }
    })
    uppy.use(ProgressBarPlugin)
  }
  uppy.use(TransloaditResults, {
    showNoResultsWarning: true,
    target: encodingResults,
    mapResult: (card: HTMLElement) => card,
  })
  uppy.use(ClearOldFiles)
  uppy.on('upload', () => {
    if (encodingResults) {
      encodingResults.innerHTML = ''
    }
    startingText?.classList.remove('hidden')
  })
  uppy.on('upload-progress', () => {
    startingText?.classList.add('hidden')
  })
  uppy.on('transloadit:complete', (completeAssembly) => {
    if (encodingResults && encodingResults.innerHTML === '') {
      uppyWrapper?.classList.remove('col-md-12')
      uppyWrapper?.classList.add('col-md-8')
      encodingResults.style.display = 'block'
      encodingResults.innerHTML = `
        <p>
          &raquo; Oops, there are no encoding results. Unless told differently, Transloadit gracefully ignores
          file types that are not supported by your encoding instructions
          in order to enable complex workflows. Did you upload the expected file type?
        </p>
      `
    }

    uppyWrapper?.classList.remove('col-md-12')
    uppyWrapper?.classList.add('col-md-8')
    if (encodingResults) {
      encodingResults.style.display = 'block'
      encodingResults.append(
        <div class="alert alert-success">
          {'All done! Typically, you would be pinged with the full '}
          <a
            target="_blank"
            rel="noreferrer noopener"
            href="https://api2.transloadit.com/assemblies/${completeAssembly.assembly_id}"
          >
            Assembly Status
          </a>
          {
            ' and you would save the references in your database, using that to display the media on your website.'
          }
        </div>,
      )
    }
  })

  // An error from the assembly
  uppy.on('transloadit:assembly-error', (assembly, err) => {
    console.error({ err, file: 'demos.ts' })

    // Hide spinner if it's visible
    const runButtonSpinner = runButton?.querySelector('.run-button-spinner')
    runButtonSpinner?.classList.add('hidden')
    if (runButton instanceof HTMLButtonElement) {
      runButton.disabled = false
    }

    function onAssemblyStatus(assemblyStatus: $TSFixMe) {
      if (!(encodingResults instanceof HTMLElement)) {
        throw new Error('encodingResults is not an HTMLElement')
      }
      if (!(uppyWrapper instanceof HTMLElement)) {
        throw new Error('uppyWrapper is not an HTMLElement')
      }
      uppyWrapper.classList.remove('col-md-12')
      uppyWrapper.classList.add('col-md-8')
      if (encodingResults) {
        encodingResults.style.display = 'block'
        encodingResults.innerHTML = `
          <strong>${assemblyStatus.error}</strong>
          <p>${assemblyStatus.message}</p>
          <p></p>
          <p>
            Something unexpected went wrong :(
            Free karma if you <a href="#" onclick="Intercom('showNewMessage', 'Hey hey! Just got an error on Assembly ${assemblyStatus.assembly_id} while testing the ${window.location.href} demo. Can you check it out? '); return false;">let us know</a> :)
          </p>
        `
      }
    }

    fetch(assembly.assembly_ssl_url).then((response) => {
      if (response.ok) {
        response
          .json()
          .then(onAssemblyStatus)
          .catch((err2) => {
            throw new Error(err2, { cause: response })
          })
      } else {
        response.text().then((text) => {
          throw new Error(text, { cause: response })
        })
      }
    })
  })
})

// Hiding the IntroBar with Transloadit brief description
const introBar = document.querySelector('.IntroBar')
const introBarClose = document.querySelector('.IntroBar-close')

if (introBar && introBarClose) {
  introBarClose.addEventListener('click', (e) => {
    e.preventDefault()
    window.localStorage.setItem('Demos.IntroBar.display', 'no')
    if (introBar instanceof HTMLElement) {
      introBar.style.display = 'none'
    }
  })
  if (window.localStorage.getItem('Demos.IntroBar.display') === 'no') {
    if (introBar instanceof HTMLElement) {
      introBar.style.display = 'none'
    }
  }
}

/**
 * `path` is a dot-separated path to where `value` should be stored in `obj`.
 * For example, setValueByPath(obj, 'params.steps.export.key', 'my-key') will set
 * `obj.params.steps.export.key` to `my-key`.
 */
function setValueByPath(obj: Record<string, unknown>, path: string, value: unknown) {
  if (!path) return obj

  const keys = path.split('.')
  let current = obj

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    const nextKey = keys[i + 1]
    const isNextKeyNumeric = !isNaN(parseInt(nextKey, 10))

    if (current[key] === undefined || current[key] === null) {
      current[key] = isNextKeyNumeric ? [] : {}
    }

    // @ts-expect-error Not sure how to type this
    current = current[key]
  }

  current[keys[keys.length - 1]] = value
  return obj
}
